import { Reducer } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  AcquiringChannel,
  Address,
  ApplicationAction,
  ApplicationActionType,
  PriceType,
  SolutionType,
} from '../../types';
import { Solution } from '../../types/application/opportunity';
import { Application } from '../../types/application/application';
import { PageName as OrderPageName } from '../../../order/pages/page-names';
import { PaymentStatus } from '../../types/application/payment';
import {
  DEVICE_NAMES,
  PRODUCT_CATEGORY_NAME_MAP,
} from 'src/features/order/tech-debt/product-details-extra';

import {
  deviceActivationSteps,
  feesSliderSteps,
  getDeliveriesAdjustedToMatchTotalSolutionQuantities,
  getStateAfterNavigateToNextStep,
  getStateAfterNavigateToPreviousStep,
  getStateAfterNextQuickEditStep,
  getStateAfterOnboardingJourneyStarted,
  getStateAfterRemovingAssignDevice,
  getStateWithPendingAppSteps,
  getStateWithStepEnabled,
  orderJourneySteps,
} from './application-reducer-helpers';
import {
  getCatalogueItemFromCatalogue,
  isECommerceInCatalogue,
  isVirtualDevice,
  requiresPayment,
} from '../../helpers/get-device-from-catalogue';
import { PageName as OnboardingPageName } from '../../../onboarding/pages/page-names';
import { CompanyType } from '../../types/application/business';
import { assignDevicesRoute } from 'src/features/order/helpers/routes';
import { Environment } from 'src/Environment';
import { CartItem } from 'src/features/common/types/application/cart';
import { AppDelivery } from 'src/features/common/types/application/delivery';
import { isECommerceItemInCart } from 'src/features/common/helpers/cart-helpers';
import { updateDeliveryArrays } from 'src/features/dpo/pages/assign-devices/helpers';
import { DeliveryOptions } from 'src/features/common/types/application/delivery-options';
import { moveDevicesToNewAddress } from 'src/features/order/helpers/delivery-helpers';

export const applicationReducer: Reducer<Application, ApplicationAction> = (
  state: Application,
  action: ApplicationAction,
) => {
  switch (action.type) {
    case ApplicationActionType.MULTIPLE_ACTIONS: {
      return action.actions.reduce((stateNew, actionInner) => {
        return processAction(stateNew, actionInner);
      }, state);
    }
    default:
      return processAction(state, action);
  }
};

function processAction(state: Application, action: ApplicationAction) {
  switch (action.type) {
    case ApplicationActionType.START_NEW: {
      const newOrderJourneySteps = [...orderJourneySteps];

      if (action.brokerLogin || action.agentJourney) {
        newOrderJourneySteps.splice(3, 0, {
          pageName: OrderPageName.ASSIGN_DEVICES,
          route: assignDevicesRoute,
          disabled: false,
          groupName: OrderPageName.ASSIGN_DEVICES,
        });
      }

      if (action.agentJourney) {
        const yourDetailsPage = newOrderJourneySteps.find(
          (page) => page.pageName === OrderPageName.YOUR_DETAILS,
        );

        if (yourDetailsPage?.pageName) yourDetailsPage.pageName = OrderPageName.CUSTOMER_DETAILS;
      }

      const newState: Application = {
        solutionType: action.solutionType,
        currentStepIndex: 0,
        steps: newOrderJourneySteps,
        payment: {
          paymentStatus: PaymentStatus.NONE,
        },
        creditVetting: {
          required: false, // -> Journey specific
        },
        brokerLogin: action.brokerLogin,
        agentJourney: action.agentJourney,
        agentId: action.agentId,
        highlightedAddress: action.highlightedAddress,
        remotePaymentsEnabled: action.remotePaymentsEnabled,
      };

      if (action.solutionType === SolutionType.buyInStoreFicaOnline && !action.agentJourney) {
        return getStateWithStepEnabled(OrderPageName.ORDER_SUMMARY, false, newState);
      }

      if (action.agentJourney && action.solutionType === SolutionType.buyInStoreFicaOnline) {
        return getStateWithStepEnabled(OrderPageName.FICA, true, newState);
      }

      return newState;
    }

    case ApplicationActionType.RESTART: {
      const newOrderJourneySteps = [...orderJourneySteps];

      if (state.brokerLogin) {
        newOrderJourneySteps.splice(3, 0, {
          pageName: OrderPageName.ASSIGN_DEVICES,
          route: assignDevicesRoute,
          disabled: true,
          groupName: OrderPageName.ASSIGN_DEVICES,
        });
      }

      const newState: Application = {
        ...state,
        solutionType: SolutionType.buyOrRentOnlineFicaOnline,
        currentStepIndex: 0,
        steps: newOrderJourneySteps,
        payment: {
          paymentStatus: PaymentStatus.NONE,
        },
        creditVetting: {
          required: false,
        },
        eCommerceRequired: false,
        stage: {
          ...state.stage,
          isOrderComplete: false,
        },
      };
      return newState;
    }

    case ApplicationActionType.UPDATE_SOLUTION_TYPE:
      if (action.solutionType === SolutionType.buyInStoreFicaOnline) {
        state = getStateWithStepEnabled(OrderPageName.ORDER_SUMMARY, false, state);
      }

      return {
        ...state,
        solutionType: action.solutionType,
      };

    case ApplicationActionType.DEVICE_ACTIVATION:
      return {
        ...state,
        deviceActivationJourney: true,
        deviceActivationTagging: true,
        solutionType: SolutionType.buyInStoreFicaOnline,
        currentStepIndex: 0,
        tapOnPhoneJourney: false,
        steps: deviceActivationSteps,
        useStockOnHandDeliveryOption: true,
      };

    case ApplicationActionType.UPDATE_CARD_MACHINE:
      return {
        ...state,
        cardMachines: action.cardMachines,
      };

    case ApplicationActionType.UPDATE_THIRD_PARTY_DEFAULT_ADDRESS_SOLUTIONS:
      return {
        ...state,
        thirdPartyDefaultAddressSolutions: action.thirdPartyDefaultAddressSolutions,
      };

    case ApplicationActionType.START_TAP_ON_PHONE_JOURNEY:
      const newState: Application = {
        ...state,
        solutionType: SolutionType.buyOrRentOnlineFicaOnline,
        currentStepIndex: 0,
        steps: orderJourneySteps,
        payment: {
          paymentStatus: PaymentStatus.NONE,
        },
        creditVetting: {
          required: action.creditVettingRequired,
        },
        passportRequired: action.passportEnabled,
        tapOnPhoneJourney: true,
      };

      return getStateWithStepEnabled(OrderPageName.CHOOSE_SOLUTION, false, newState);

    case ApplicationActionType.UPDATE_BASKET_QUANTITY: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(`App reducer revamp: unknown product: product id: ${action.productId}`);
      }

      const solutionNew: Solution = {
        productId: action.productId,
        acquiringChannel: action.acquiringChannel,
        quantity: action.quantity,
      };

      let solutionNewAdded = false;

      const solutionsUpdated = (state.opportunity?.solutions || []).map((solution) => {
        if (
          solution.productId === solutionNew.productId &&
          solution.acquiringChannel === solutionNew.acquiringChannel
        ) {
          solutionNewAdded = true;
          return solutionNew;
        } else {
          return solution;
        }
      });

      if (!solutionNewAdded) {
        solutionsUpdated.push(solutionNew);
      }

      const newState = {
        ...state,
        opportunity: {
          ...state.opportunity,
          solutions: solutionsUpdated,
          deliveries: getDeliveriesAdjustedToMatchTotalSolutionQuantities(
            solutionsUpdated,
            state.opportunity?.deliveries || [],
          ),
        },
      };

      // 3PO Broker payment
      if (
        !!state.brokerLogin &&
        (actionCatalogueItem.device.title === DEVICE_NAMES.VODAPAY_KWIKA ||
          actionCatalogueItem.device.title === DEVICE_NAMES.MAX_2)
      ) {
        state = getStateWithStepEnabled(OrderPageName.PAY, false, state);

        state = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, true, state);
        state = getStateWithStepEnabled(OrderPageName.PAY_STATUS, true, state);

        newState.steps = [...(state.steps || [])];
      }

      const thirdPartyCreditVetting =
        (!!state.brokerLogin || state.agentJourney) &&
        actionCatalogueItem.price.priceType === PriceType.MONTHLY;

      if (
        !Environment.current?.disabledFeatures?.creditVetting &&
        (actionCatalogueItem.device.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline ||
          thirdPartyCreditVetting)
      ) {
        newState.creditVetting = {
          ...state.creditVetting,
          required: action.quantity > 0,
        };
      }

      if (isECommerceInCatalogue(action.catalogue, solutionsUpdated)) {
        newState.eCommerceRequired = action.quantity > 0;
        return getStateWithStepEnabled(OrderPageName.PAYMENT_TYPE, action.quantity > 0, newState);
      }

      return newState;
    }

    case ApplicationActionType.UPDATE_DELIVERY_OPTIONS: {
      const { isDelivery, device } = action;

      // Create copies of arrays to avoid mutation
      let deliveries_ = [...(state.deliveries || [])];
      let collections_ = [...(state.collections || [])];

      const { deliveries, collections } = updateDeliveryArrays(
        deliveries_,
        collections_,
        device,
        isDelivery,
      );

      return {
        ...state,
        deliveries,
        collections,
      };
    }

    case ApplicationActionType.UPDATE_CART_QUANTITY: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(`App reducer revamp: unknown product: product id: ${action.productId}`);
      }

      let updatedCollections = state.collections ? [...state.collections] : [];
      let updatedDeliveries = state.deliveries ? [...state.deliveries] : [];
      const oldCartQuantity =
        (state.cart || []).find(
          (item) =>
            item.productId === action.productId &&
            item.acquiringChannel === action.acquiringChannel,
        )?.quantity || 0;

      const newCartItem: CartItem = {
        productId: action.productId,
        acquiringChannel: action.acquiringChannel,
        title: actionCatalogueItem.device.title,
        quantity: action.quantity,
        price: actionCatalogueItem.price.price,
        tagline: actionCatalogueItem.device.tagline,
        priceType: actionCatalogueItem.price.priceType,
        deliveryOptions: actionCatalogueItem.device.deliveryOptions || [],
        image: actionCatalogueItem.device.imageBase64 || '',
      };

      let newCartItemAdded = false;

      const updatedCartItems = (state.cart || []).map((item) => {
        if (
          item.productId === newCartItem.productId &&
          item.acquiringChannel === newCartItem.acquiringChannel
        ) {
          newCartItemAdded = true;
          return newCartItem;
        }
        return item;
      });

      // Find the index of the item in the deliveries array
      const defaultDeliveryIndex = updatedDeliveries.findIndex(
        (delivery) => delivery.productId === action.productId && delivery.addressId === 'default',
      );

      let assignedToDeliveries = false;

      if (
        defaultDeliveryIndex === -1 &&
        newCartItem.deliveryOptions.includes(DeliveryOptions.Deliver) &&
        !isVirtualDevice(newCartItem.title)
      ) {
        // If the item doesn't exist and has more than one delivery option, add it
        updatedDeliveries.push({
          id: uuidv4(),
          productId: action.productId,
          addressId: 'default',
          quantity: 1,
        });
        assignedToDeliveries = true;
      }

      // If the item exists, update its quantity
      if (
        defaultDeliveryIndex !== -1 &&
        newCartItem.deliveryOptions.includes(DeliveryOptions.Deliver) &&
        !isVirtualDevice(newCartItem.title)
      ) {
        assignedToDeliveries = true;
        updatedDeliveries[defaultDeliveryIndex].quantity +=
          action.quantity > oldCartQuantity ? 1 : -1;
      }

      // if it's broker or agent journey, update collections arrays
      if (!!state.brokerLogin || state.agentJourney) {
        const defaultCollectionIndex = updatedCollections.findIndex(
          (collection) =>
            collection.productId === action.productId && collection.addressId === 'default',
        );

        if (
          defaultCollectionIndex !== -1 &&
          !assignedToDeliveries &&
          !isVirtualDevice(newCartItem.title)
        ) {
          // If the item exists, update its quantity
          updatedCollections[defaultCollectionIndex].quantity +=
            action.quantity > oldCartQuantity ? 1 : -1;
        }

        if (
          newCartItem.deliveryOptions.length === 1 &&
          newCartItem.deliveryOptions.includes(DeliveryOptions.Stock) &&
          !assignedToDeliveries &&
          !isVirtualDevice(newCartItem.title)
        ) {
          // If the item doesn't exist and has only one delivery option, add it
          updatedCollections.push({
            id: uuidv4(),
            productId: action.productId,
            addressId: 'default',
            quantity: 1,
          });
        }
      }

      if (!newCartItemAdded) {
        updatedCartItems.push(newCartItem);
      }

      const newState: Application = {
        ...state,
        cart: updatedCartItems,
        deliveries: updatedDeliveries,
        collections: updatedCollections,
      };

      if (isECommerceItemInCart(newState.cart || [], DEVICE_NAMES.VODAPAY_ECOM_GATEWAY)) {
        newState.eCommerceRequired = action.quantity > 0;
        return getStateWithStepEnabled(OrderPageName.PAYMENT_TYPE, action.quantity > 0, newState);
      }

      return newState;
    }

    case ApplicationActionType.REMOVE_CART_QUANTITY: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(`App reducer revamp: unknown product: product id: ${action.productId}`);
      }

      const updatedCollections = state.collections ? [...state.collections] : [];
      const updatedDeliveries = state.deliveries ? [...state.deliveries] : [];

      const cartItemsAfterRemove = (state.cart || []).filter((item) => {
        const cartCatalogueItem = getCatalogueItemFromCatalogue(
          action.catalogue,
          item.productId,
          action.acquiringChannel,
        );

        if (!cartCatalogueItem) {
          throw new Error(`App reducer revamp: unknown product: ${item.productId}`);
        }

        return !(
          (item.productId === action.productId &&
            item.acquiringChannel === action.acquiringChannel) ||
          (actionCatalogueItem.device.productCategoryName &&
            actionCatalogueItem.device.productCategoryName ===
              cartCatalogueItem.device.productCategoryName)
        );
      });

      // Remove the item from the deliveries array
      const defaultDeliveryIndex = updatedDeliveries.findIndex(
        (delivery) => delivery.productId === action.productId,
      );

      if (defaultDeliveryIndex !== -1) {
        updatedDeliveries.splice(defaultDeliveryIndex, 1);
      }

      // if it's broker or agent journey, remove the item from collections arrays
      if (!!state.brokerLogin || state.agentJourney) {
        const defaultCollectionIndex = updatedCollections.findIndex(
          (collection) => collection.productId === action.productId,
        );

        if (defaultCollectionIndex !== -1) {
          updatedCollections.splice(defaultCollectionIndex, 1);
        }
      }

      const newState: Application = {
        ...state,
        cart: cartItemsAfterRemove,
        deliveries: updatedDeliveries,
        collections: updatedCollections,
      };

      if (!isECommerceItemInCart(newState.cart || [], DEVICE_NAMES.VODAPAY_ECOM_GATEWAY)) {
        newState.eCommerceRequired = isECommerceItemInCart(
          newState.cart || [],
          DEVICE_NAMES.VODAPAY_ECOM_GATEWAY,
        );
        return getStateWithStepEnabled(
          OrderPageName.PAYMENT_TYPE,
          isECommerceItemInCart(newState.cart || [], DEVICE_NAMES.VODAPAY_ECOM_GATEWAY),
          newState,
        );
      }

      return newState;
    }

    case ApplicationActionType.DELETE_ADDRESS:
      const deviceAssignedToAddressToDelete = (state.deliveries ?? []).filter(
        (device) => device.addressId === action.addressId,
      );
      const addresses = state.addresses?.filter((address) => address.id !== action.addressId);

      const deliveries = moveDevicesToNewAddress(
        state.deliveries || [],
        deviceAssignedToAddressToDelete,
        'default',
        action.addressId,
      );

      // If it is third party include collections
      if (!!state.brokerLogin || state.agentJourney) {
        const deviceAssignedToAddressToDeleteCollect = (state.collections ?? []).filter(
          (device) => device.addressId === action.addressId,
        );

        const collections = moveDevicesToNewAddress(
          state.collections || [],
          deviceAssignedToAddressToDeleteCollect,
          'default',
          action.addressId,
        );

        return {
          ...state,
          addresses,
          collections,
          deliveries,
        };
      }

      return {
        ...state,
        addresses,
        deliveries,
      };

    case ApplicationActionType.ADD_ADDRESS: {
      const newAddress: Address = {
        id: action.address.id ?? 'default',
        street: action.address.street,
        building: action.address.building,
        suburb: action.address.suburb,
        city: action.address.city,
        province: action.address.province,
        country: action.address.country ?? 'South Africa',
        postalCode: action.address.postalCode,
        recipient: action.recipient,
      };

      return {
        ...state,
        addresses: [...(state.addresses || []), newAddress],
      };
    }

    case ApplicationActionType.UPDATE_ADDRESS:
      if (!action.address.id) return state;

      const updatedAddress = (state.addresses || []).map((address) => {
        if (address.id === action.address.id)
          return {
            ...action.address,
            recipient: action.recipient,
          };

        return address;
      });

      return {
        ...state,
        addresses: updatedAddress,
      };

    case ApplicationActionType.ADD_DELIVERY_ADDRESS: {
      const newAddress: Address = {
        id: action.address.id ?? 'default',
        street: action.address.street,
        building: action.address.building,
        suburb: action.address.suburb,
        city: action.address.city,
        province: action.address.province,
        country: action.address.country ?? 'South Africa',
        postalCode: action.address.postalCode,
      };

      let addressUpdated = false;

      const newAddresses = (state?.addresses ?? []).map((address) => {
        if (address.id === newAddress.id) {
          addressUpdated = true;
          return newAddress;
        }
        return address;
      });

      if (!addressUpdated) {
        newAddresses.push(newAddress);
      }

      const updatedStateDeliveries: AppDelivery[] = (state?.deliveries ?? []).map((delivery) => {
        const product = action.products.find((product) => product.id === delivery.productId);
        if (product) {
          return { ...delivery, quantity: delivery.quantity - product.quantity };
        }
        return delivery;
      });

      const deliveries: AppDelivery[] = action.products.map(({ id, quantity }) => ({
        id: uuidv4(),
        productId: id,
        addressId: newAddress.id ?? 'default',
        quantity,
      }));

      const newState: Application = {
        ...state,
        addresses: newAddresses,
        deliveries: [...updatedStateDeliveries, ...deliveries],
      };
      return newState;
    }

    case ApplicationActionType.UPDATE_DELIVERY_ADDRESS: {
      const newAddress: Address = {
        id: action.address.id ?? 'default',
        street: action.address.street,
        building: action.address.building,
        suburb: action.address.suburb,
        city: action.address.city,
        province: action.address.province,
        country: action.address.country ?? 'South Africa',
        postalCode: action.address.postalCode,
      };

      let addressUpdated = false;

      const newAddresses = (state?.addresses ?? []).map((address) => {
        if (address.id === newAddress.id) {
          addressUpdated = true;
          return newAddress;
        }
        return address;
      });

      if (!addressUpdated) {
        newAddresses.push(newAddress);
      }

      const updatedDeliveries = (state.deliveries ?? []).map((delivery) => {
        if (delivery.addressId === newAddress.id) {
          const updatedProduct = action.products.find(
            (product) => product.id === delivery.productId,
          );
          if (updatedProduct) {
            return { ...delivery, quantity: updatedProduct.quantity };
          }
        }
        return delivery;
      });

      // find all deliveries for a product
      action.products.forEach((product) => {
        // calculate the total number of products that are not being delivered to the default address
        const number = updatedDeliveries.reduce((total, delivery) => {
          if (delivery.addressId !== 'default' && delivery.productId === product.id) {
            return total + delivery.quantity;
          }
          return total + 0;
        }, 0);

        const defaultDeliveryIndex = updatedDeliveries.findIndex(
          (delivery) => delivery.addressId === 'default' && delivery.productId == product.id,
        );

        const productCart = (state.cart ?? []).find((item) => item.productId === product.id);

        if (productCart) {
          updatedDeliveries[defaultDeliveryIndex].quantity = productCart.quantity - number;
        }
      });

      const newState: Application = {
        ...state,
        addresses: newAddresses,
        deliveries: updatedDeliveries,
      };
      return newState;
    }

    case ApplicationActionType.UPDATE_DEVICE_DELIVERIES:
      return {
        ...state,
        deliveries: action.deliveries,
      };

    case ApplicationActionType.UPDATE_DEVICE_COLLECTIONS:
      return {
        ...state,
        collections: action.collections,
      };

    case ApplicationActionType.REMOVE_BASKET_QUANTITY: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(`App reducer revamp: unknown product: product id: ${action.productId}`);
      }

      const solutionsAfterRemove = (state.opportunity?.solutions || []).filter((solution) => {
        const solutionCatalogueItem = getCatalogueItemFromCatalogue(
          action.catalogue,
          solution.productId,
          action.acquiringChannel,
        );

        if (!solutionCatalogueItem) {
          throw new Error(`App reducer revamp: unknown product: ${solution.productId}`);
        }

        return !(
          (solution.productId === action.productId &&
            solution.acquiringChannel === action.acquiringChannel) ||
          (actionCatalogueItem.device.productCategoryName &&
            actionCatalogueItem.device.productCategoryName ===
              solutionCatalogueItem.device.productCategoryName)
        );
      });

      const newState = {
        ...state,
        opportunity: {
          ...state.opportunity,
          solutions: solutionsAfterRemove,
          deliveries: getDeliveriesAdjustedToMatchTotalSolutionQuantities(
            solutionsAfterRemove,
            state.opportunity?.deliveries || [],
          ),
        },
      };

      if (!!state.brokerLogin && actionCatalogueItem.device.title === DEVICE_NAMES.VODAPAY_KWIKA) {
        state = getStateWithStepEnabled(OrderPageName.PAY, true, state);
        state = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, false, state);
        state = getStateWithStepEnabled(OrderPageName.PAY_STATUS, false, state);
        newState.steps = [...(state.steps || [])];
      }

      const thirdPartyCreditVetting =
        (!!state.brokerLogin || state.agentJourney) &&
        actionCatalogueItem.device.secondaryAcquiringChannel ===
          AcquiringChannel.rentOnlineFicaOnline;

      if (
        !Environment.current?.disabledFeatures?.creditVetting &&
        (actionCatalogueItem.device.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline ||
          thirdPartyCreditVetting)
      ) {
        newState.creditVetting = {
          ...state.creditVetting,
          required: false,
        };
      }

      if (!isECommerceInCatalogue(action.catalogue, solutionsAfterRemove)) {
        newState.eCommerceRequired = isECommerceInCatalogue(action.catalogue, solutionsAfterRemove);
        return getStateWithStepEnabled(
          OrderPageName.PAYMENT_TYPE,
          isECommerceInCatalogue(action.catalogue, solutionsAfterRemove),
          newState,
        );
      }

      return newState;
    }

    case ApplicationActionType.UPDATE_CONTACT_DETAILS:
      // VB POS: Add city to deliveries
      state.opportunity = {
        ...state.opportunity,
        deliveries: state.opportunity?.deliveries?.map((delivery) => {
          if (!delivery.address.city) {
            delivery.address.city = action.contactDetails.businessAddress?.city;
          }

          return delivery;
        }),
      };

      return {
        ...state,
        contact: {
          ...state.contact,
          ...action.contactDetails,
        },
      };

    case ApplicationActionType.UPDATE_REFERENCE_NUMBER:
      return {
        ...state,
        referenceNumber: action.referenceNumber,
      };

    case ApplicationActionType.UPDATE_PAYMENT_TYPE: {
      if (!!state.brokerLogin && !action.onlinePaymentRequired) {
        state = getStateWithStepEnabled(OrderPageName.PAY, true, state);

        state = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, false, state);
        state = getStateWithStepEnabled(OrderPageName.PAY_STATUS, false, state);

        state.steps = [...(state.steps || [])];
      }

      return {
        ...state,
        payment: {
          ...state.payment,
          paymentType: action.paymentType,
          onlinePaymentRequired: action.onlinePaymentRequired,
        },
      };
    }

    case ApplicationActionType.UPDATE_PAYMENT_STATUS:
      return {
        ...state,
        payment: {
          ...state.payment,
          paymentStatus: action.paymentStatus,
          orderNumber: action.orderNumber,
        },
      };

    case ApplicationActionType.UPDATE_DOCUMENTS:
      return {
        ...state,
        documents: action.documents,
      };

    case ApplicationActionType.NAVIGATE_TO_NEXT_STEP:
      if (state.quickEdit?.returnIndex !== undefined) {
        if (!state.quickEdit?.steps || state.quickEdit?.steps.length === 0) {
          return {
            ...state,
            currentStepIndex: state.quickEdit.returnIndex,
            quickEdit: {
              returnIndex: undefined,
            },
          };
        }

        return getStateAfterNextQuickEditStep(state.quickEdit.steps, state, true);
      }

      if (
        state.steps &&
        state.currentStepIndex &&
        (state.steps[state.currentStepIndex].pageName === OrderPageName.PAY ||
          state.steps[state.currentStepIndex].pageName === OrderPageName.PAY_STATUS)
      ) {
        return getStateAfterOnboardingJourneyStarted(state);
      }

      return getStateAfterNavigateToNextStep(state);

    case ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP:
      return getStateAfterNavigateToPreviousStep(state);

    case ApplicationActionType.UPDATE_DIRECTOR_DETAILS:
      return {
        ...state,
        directors: action.directorDetails,
      };

    case ApplicationActionType.UPDATE_DELIVERIES:
      return {
        ...state,
        opportunity: {
          ...state.opportunity,
          deliveries: action.deliveries,
        },
      };

    case ApplicationActionType.HIGHLIGHT_ADDRESS:
      return {
        ...state,
        highlightedAddress: action.highlightedAddress,
      };

    case ApplicationActionType.UPDATE_BUSINESS_DETAILS: {
      const newState = {
        ...state,
        businessDetails: action.businessDetails,
      };

      const isSolePropNotSelected = action.businessDetails.companyType !== CompanyType.SOLE_PROP;

      return getStateWithStepEnabled(
        OnboardingPageName.DIRECTOR_DETAILS,
        isSolePropNotSelected,
        newState,
      );
    }

    case ApplicationActionType.UDPATE_RESIDENTIAL_ADDRESS: {
      return {
        ...state,
        contact: {
          ...state.contact,
          residentialAddress: action.address,
          isResidentialAddressSameAsBusinessAddress:
            action.isResidentialAddressSameAsBusinessAddress,
        },
      };
    }
    case ApplicationActionType.UPDATE_BANK_DETAILS:
      return {
        ...state,
        bankDetails: action.bankDetails,
      };

    case ApplicationActionType.UPDATE_CREDIT_VETTING_STATUS:
      return {
        ...state,
        creditVetting: {
          ...state.creditVetting,
          bankAccountValidationStatus: action.bankAccountValidationStatus,
          bankAccountValidationAttempts: action.bankAccountValidationAttempts,
        },
      };

    case ApplicationActionType.KILL_APPLICATION:
      return {
        ...state,
        killApplication: true,
      };

    case ApplicationActionType.QUICK_EDIT: {
      return getStateAfterNextQuickEditStep(action.steps, state, false);
    }

    case ApplicationActionType.SET_WHOLE_APP:
      let applicationNew = {
        ...action.application,
      };

      if (applicationNew.deviceActivationJourney) {
        applicationNew = {
          ...applicationNew,
          payment: {
            paymentStatus: PaymentStatus.PAYMENT_SUCCESSFUL,
          },
        };
      }

      if (applicationNew.solutionType === SolutionType.thirdPartyOnboarding && action.dealerId) {
        applicationNew.brokerLogin = action.dealerId;
        applicationNew.remotePaymentsEnabled = action.remotePaymentsEnabled;

        if (applicationNew.remotePayment?.transactionId) {
          applicationNew.remotePayment.initiated = true;
        }

        // 3PO Broker payment
        if (requiresPayment(action.catalogue)) {
          let newState = getStateWithStepEnabled(OrderPageName.PAY, false, applicationNew);

          newState = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, true, newState);
          newState = getStateWithStepEnabled(OrderPageName.PAY_STATUS, true, newState);

          applicationNew.steps = [...(newState.steps || [])];
        }

        if (!requiresPayment(action.catalogue)) {
          applicationNew = getStateWithStepEnabled(OrderPageName.PAY, true, applicationNew);
          applicationNew = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, false, applicationNew);
          applicationNew = getStateWithStepEnabled(OrderPageName.PAY_STATUS, false, applicationNew);

          applicationNew.steps = [...(applicationNew.steps || [])];
        }
      }

      if (!!applicationNew.brokerLogin) {
        const newState = getStateWithStepEnabled(
          OrderPageName.ASSIGN_DEVICES,
          true,
          applicationNew,
        );
        applicationNew.steps = [...(newState.steps || [])];
      }

      const solutions = applicationNew.opportunity?.solutions || [];
      if (applicationNew.eCommerceRequired) {
        const isPaymentGatewaySelected: boolean = !!solutions.find((solution) => {
          const catalogueItem = getCatalogueItemFromCatalogue(
            action.catalogue,
            solution.productId,
            solution.acquiringChannel,
          );
          return !!PRODUCT_CATEGORY_NAME_MAP[catalogueItem?.device?.productCategoryName ?? ''];
        });
        applicationNew.eCommerceRequired = isPaymentGatewaySelected;

        applicationNew = getStateWithStepEnabled(
          OrderPageName.PAYMENT_TYPE,
          isPaymentGatewaySelected,
          applicationNew,
        );
      }

      if (!Environment.current?.disabledFeatures?.creditVetting) {
        const isCreditVettingEnabled: boolean = !!solutions.find((solution) => {
          const catalogueItem = getCatalogueItemFromCatalogue(
            action.catalogue,
            solution.productId,
            solution.acquiringChannel,
          );
          return catalogueItem?.device?.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline;
        });
        if (applicationNew?.creditVetting) {
          applicationNew.creditVetting.required = isCreditVettingEnabled;
        }

        applicationNew = getStateWithStepEnabled(
          OnboardingPageName.RES_ADDRESS,
          isCreditVettingEnabled,
          applicationNew,
        );
      }

      const isSolePropNotSelected =
        applicationNew.businessDetails?.companyType !== CompanyType.SOLE_PROP;

      applicationNew = getStateWithStepEnabled(
        OnboardingPageName.DIRECTOR_DETAILS,
        isSolePropNotSelected,
        { ...applicationNew, currentStepIndex: action.application.currentStepIndex },
      );

      if (applicationNew.pendingApp?.isPending) {
        applicationNew = getStateWithPendingAppSteps(applicationNew);
      }

      return applicationNew;

    case ApplicationActionType.UPDATE_CARD_FEES:
      return {
        ...state,
        opportunity: {
          ...state.opportunity,
          cardFees: action.cardFees,
        },
      };

    case ApplicationActionType.UPDATE_SALES_DETAILS:
      return {
        ...state,
        salesDetails: {
          organisation: action.organisation,
          agent: action.agent,
        },
      };

    case ApplicationActionType.UPDATE_REMOTE_PAYMENT_STATUS:
      return {
        ...state,
        remotePayment: {
          ...action,
          numberOfTries: action.isNew
            ? (state.remotePayment?.numberOfTries || 0) + 1
            : state.remotePayment?.numberOfTries || 0,
        },
      } as Application;

    case ApplicationActionType.RESET_REMOTE_PAYMENT_STATUS:
      return {
        ...state,
        remotePayment: {
          initiated: false,
          transactionId: '',
          responseCode: '',
          responseMessage: '',
          numberOfTries: state.remotePayment?.numberOfTries,
        },
      } as Application;

    case ApplicationActionType.ATTACH_SERIAL_NUMBER:
      const updatedSolutions = state.opportunity?.solutions?.map((solution) => {
        if (solution.productId === action.productId && solution.quantity > 0) {
          return {
            ...solution,
            serialNumbers: [...(solution.serialNumbers || []), ...action.serialNumber],
            ...(action.friendlyTerminalName && {
              friendlyTerminalName: action.friendlyTerminalName,
            }),
          };
        }

        return solution;
      });

      return {
        ...state,
        opportunity: {
          ...state.opportunity,
          solutions: updatedSolutions,
          deliveries: getDeliveriesAdjustedToMatchTotalSolutionQuantities(
            updatedSolutions || [],
            state.opportunity?.deliveries || [],
          ),
        },
      } as Application;

    case ApplicationActionType.ADD_PAYMENT_METHOD_CODE: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(
          `App reducer revamp(ADD_PAYMENT_METHOD_CODE): unknown product: product id: ${action.productId}`,
        );
      }

      const solutionsUpdated = (state.opportunity?.solutions || []).map((solution) => {
        if (
          solution.productId === action.productId &&
          solution.acquiringChannel === action.acquiringChannel
        ) {
          return { ...solution, paymentMethodCode: action.paymentMethodCode };
        }
        return solution;
      });

      const newState: Application = {
        ...state,
        opportunity: {
          ...state.opportunity,
          solutions: solutionsUpdated,
        },
      };

      return newState;
    }

    case ApplicationActionType.SHOW_DEVICE_ACTIVATION: {
      const hasOnlyVirtualDevices = action.productItems.every((item) =>
        isVirtualDevice(item.device.title),
      );

      if (!!state.brokerLogin) {
        return getStateAfterRemovingAssignDevice(
          OrderPageName.ASSIGN_DEVICES,
          !hasOnlyVirtualDevices,
          state,
        );
      }
      return state;
    }
    case ApplicationActionType.FEE_SLIDER:
      return {
        ...state,
        feeSlider: true,
        solutionType: SolutionType.buyInStoreFicaOnline,
        currentStepIndex: 0,
        tapOnPhoneJourney: false,
        steps: feesSliderSteps,
      };

    case ApplicationActionType.SET_DPO: {
      return {
        ...state,
        dpo: action.dpoEnabled,
      };
    }

    default:
      return state;
  }
}
